import 'whatwg-fetch'
import { languageChange } from './state/Login';
import { errorModal } from './state/Error';
import { sessionExpired, loggedOut } from './state/Login';
import { getIsAppOrigin } from './Utils';
import { getAccessToken } from './auth/accessToken';

function mustGetEnv(name) {
    const value = process.env[name];
    if (value === undefined || value === '') {
        throw new Error(`Missing required environment variable ${name}`);
    }
    return value;
}

const baseUrl = mustGetEnv('REACT_APP_API_URL');
const apiKey = mustGetEnv('REACT_APP_API_KEY');

export const url = `${baseUrl}?apiKey=${apiKey}`;
export const signUrls = {
    idCard: 'https://sign.olerex.ee/sign/idcard',
    mobileId: 'https://sign.olerex.ee/sign/mobileid',
    smartId: 'https://sign.olerex.ee/sign/smartid'
};


const request = (dispatch, url, body, successCallback, errorCallback, ignoreErrorModal = false, method = 'POST') => {
    window.fetch(url,
        {
            method,
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            },
            body: method === 'GET' ? undefined : JSON.stringify(body)
        })
        .then(response => response.json())
        .then(data => {
            if (data && data.records && data.records.tokenExpired === 1) {
                dispatch(sessionExpired());
            } else {
                if (data && data.records && data.records.statusCode !== undefined && data.records.statusCode !== 200) {
                    if (data.status && !(data.status.act === "olerexweb.business.cards" || data.status.act === "olerexweb.business.manage" || data.status.act === 'olerexweb.validate')) {
                        if (!ignoreErrorModal) {
                            dispatch(errorModal(true));
                        }
                        if (errorCallback) {
                            errorCallback(data);
                        }
                        return;
                    }
                }
                if (data && data.records && data.records.language) {
                    dispatch(languageChange(data.records.language));
                }
                if (successCallback) {
                    successCallback(data);
                }
            }
        })
        .catch(error => {
            if (!ignoreErrorModal) {
                dispatch(errorModal(true));
            }
            if (errorCallback) {
                errorCallback(error);
            } else {
                console.error('Unhandled request error', error);
            }
        });
};

// Authenticated calls source the token from the single in-memory owner instead
// of relying on each caller to thread it through the body. The owner mirrors
// redux `login.token` (see index.js), so the injected value is identical to what
// callers pass today — this is behaviour-preserving; it just makes request() the
// one place the token is attached, ready for the planned refresh interceptor.
const authedRequest = (dispatch, requestUrl, body, success, error, ignoreErrorModal = false, method = 'POST') =>
    request(dispatch, requestUrl, body ? { ...body, token: getAccessToken() } : body, success, error, ignoreErrorModal, method);

export const authRequest = (dispatch, body, success, error) => request(dispatch, `${url}&act=olerexweb.auth`, body, success, error);
export const getActiveSessionsRequest = (dispatch, body, success, error) => request(dispatch, `${url}&act=olerexweb.getActiveSessions&token=${getAccessToken()}&userId=${body.userId || ''}&accNr=${body.accNr || ''}`, body, success, error, false, 'GET');

// Ends the server session of this origin (the app and the web have their own) and logs out
// here. Shared by the dashboard and the set-password gate, which has no dashboard to log out from.
// Whatever the server answers, the user asked to leave, so every path ends in loggedOut —
// the gate has no error modal, and a failed lookup there used to leave the button dead.
export const logout = (dispatch, login, done) => {
    const role = login.currentRole;
    const localLogout = () => {
        dispatch(loggedOut());
        if (done) done();
    };

    getActiveSessionsRequest(dispatch, {
        userId: role ? role.userId : null,
        accNr: role ? role.accnr : null,
        token: login.token
    }, (res) => {
        if (res.records.statusCode !== 200) {
            dispatch(loggedOut());

        } else if (res.records.sessions.length === 0) {
            dispatch(loggedOut());
            if (window.webViewBridge) {
                window.webViewBridge.send('logout');
            }

        } else {
            const isApp = getIsAppOrigin();
            const currentSession = res.records.sessions.find(s =>
                isApp ? s.session_name === 'ÄPP' : s.session_name !== 'ÄPP'
            ) || res.records.sessions[0];

            endSession(dispatch, currentSession.apiCall, localLogout);
        }
    }, localLogout);
};

export const endSession = (dispatch, apiCall, done) => {
    fetch(apiCall)
        .then(res => res.json())
        .then(data => {
            if (data.records.reload) {
                dispatch(loggedOut());
                window.location.reload();
            }
            if (data.records.function === 'logoutApp') {
                dispatch(loggedOut());
                if (window.webViewBridge) {
                    window.webViewBridge.send('logout');
                }
            }
            if (done) done();
        })
        .catch(() => {
            if (done) done();
        });
};

export const inviteRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.virtualFamilyInvite`, body, success, error);
export const updateUserData = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.private.updateUserData`, body, success, error);
export const setPasswordRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.setPassword`, body, success, error, true);

export const businessDashboardRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.business.dashboard`, body, success, error);
export const businessCardsRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.business.cards`, body, success, error);
export const validateAddCardRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.validate`, body, success, error);
export const businessInvoicesRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.business.invoices`, body, success, error);
export const businessBanklinksRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.business.banklink`, body, success, error);
export const businessTransactionsRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.business.transactions`, body, success, error);
export const businessManageRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.business.manage`, body, success, error);
export const businessVirtualFamilyRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.business.virtualfamily`, body, success, error);
export const businessWholeSaleRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.wholeSale`, body, success, error);

export const privateDashboardRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.private.dashboard`, body, success, error);
export const setLanguageRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.setLanguage`, body, success, error);
export const privateUpdateUserDataRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.private.updateUserData`, body, success, error, body.ignoreErrorModal);
export const privateTransactionsRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.private.transactions`, body, success, error);
export const privateVouchersRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.private.vouchers`, body, success, error, body.ignoreErrorModal);
export const privateInvoicesRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.business.invoices`, body, success, error); //business.invoices not private.invoices

export const privateElectricityRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.private.electricity`, body, success, error);

// ID credit application (self-service port of idcredit.olerex.ee). Mirrors the
// electricity contract flow: generateContract -> getSignData -> finishContract.
// Signing itself reuses SignContractModal (sign.olerex.ee), identical to electricity.
export const privateIdCreditApplicationRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.private.idCreditApplication`, body, success, error, body.ignoreErrorModal);

// Yearly contact data check for ID credit customers (ContactConfirmation).
export const privateContactConfirmationRequest = (dispatch, body, success, error) => authedRequest(dispatch, `${url}&act=olerexweb.private.contactConfirmation`, body, success, error, body.ignoreErrorModal);

export const getTicketsRequest = (dispatch, personalCode, success, error) => request(dispatch, `${url}&act=olerexweb.travel.getTickets&personalCode=${personalCode}`, null, success, error);

const gatewayBaseUrl = process.env.REACT_APP_GATEWAY_URL || 'https://gateway.olerex.ee';
const ssoBaseUrl = process.env.REACT_APP_SSO_URL || '/sso';

export const gatewayRequest = (dispatch, path, jwtToken, body, successCallback, errorCallback, method = 'POST') => {
    const options = {
        method,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${jwtToken}`,
        },
    };
    if (method !== 'GET' && body) {
        options.body = JSON.stringify(body);
    }

    window.fetch(`${gatewayBaseUrl}${path}`, options)
        .then(response => response.json())
        .then(data => {
            if (successCallback) {
                successCallback(data);
            }
        })
        .catch(error => {
            if (errorCallback) {
                errorCallback(error);
            } else {
                console.error('Gateway request error', error);
            }
        });
};

export const ssoRequest = (dispatch, path, formData, successCallback, errorCallback) => {
    // Convert FormData to URLSearchParams to avoid multipart encoding issues
    const params = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
        params.append(key, value);
    }
    window.fetch(`${ssoBaseUrl}${path}`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params,
        credentials: 'include',
    })
        .then(response => response.json())
        .then(data => {
            if (successCallback) successCallback(data);
        })
        .catch(error => {
            if (errorCallback) {
                errorCallback(error);
            } else {
                console.error('SSO request error', error);
            }
        });
};
